import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { Link, useParams, useOutletContext } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { Icon } from "../app/Icon";
import { initials } from "../profiles/ProfileMenu";
import { listMessages, sendMessage, type Message, type Conversation as Convo } from "../lib/messaging";

function clock(at?: string): string {
  if (!at) return "";
  return new Date(at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export function Conversation() {
  const { id } = useParams();
  const ctx = useOutletContext<{ conversations: Convo[] } | null>();
  const other = ctx?.conversations.find((c) => c.id === id)?.other_name;

  const [messages, setMessages] = useState<Message[]>([]);
  const [me, setMe] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  async function load() {
    if (!id) return;
    try {
      const r = await listMessages(id);
      setMessages(r.messages);
      setMe(r.me);
    } catch {
      setError("Could not load this conversation.");
    }
  }

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length]);

  async function onSend(e: FormEvent) {
    e.preventDefault();
    if (!id || !body.trim()) return;
    setBusy(true);
    try {
      await sendMessage({ conversation_id: id, body });
      setBody("");
      await load();
    } finally {
      setBusy(false);
    }
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void onSend(e as unknown as FormEvent);
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Thread header */}
      <div className="flex items-center gap-3 border-b border-line px-4 py-3">
        <Link to="/messages" className="text-muted hover:text-ink lg:hidden" aria-label="Back to conversations">
          <Icon name="chevron" className="h-5 w-5 rotate-90" />
        </Link>
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-accent/15 font-display text-xs font-black text-accent-strong">{initials(other ?? "?")}</span>
        <span className="truncate font-display text-base font-black tracking-tight">{other ?? "Conversation"}</span>
      </div>

      {/* Messages */}
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-4">
        {error && <p className="font-mono text-sm text-danger">{error}</p>}
        {messages.map((m) => {
          const mine = m.sender_id === me;
          return (
            <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
              <span className={`inline-block max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-accent text-accent-ink" : "border border-line bg-surface-2"}`}>
                {m.body}
              </span>
              <span className="mt-0.5 px-1 font-mono text-[10px] text-muted">{clock(m.created_at)}</span>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {/* Composer */}
      <form className="border-t border-line p-3" onSubmit={onSend}>
        <div className="flex items-end gap-2">
          <textarea
            className="max-h-32 min-h-[2.5rem] flex-1 resize-none rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-ink"
            rows={1}
            placeholder="Type a message…"
            aria-label="Message"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={onKey}
            maxLength={2000}
          />
          <Button type="submit" disabled={busy || !body.trim()}>Send</Button>
        </div>
        <p className="mt-1.5 px-1 font-mono text-[10px] text-muted">Press Enter to send · Shift + Enter for new line</p>
      </form>
    </div>
  );
}
